import { useEffect, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { Database, Radar, FileCheck2, HardDrive, ShieldCheck, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, CountUp } from "./ui";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="card-elevated p-6 group" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground uppercase tracking-widest">{label}</span>
        <div className={`h-8 w-8 rounded-sm flex items-center justify-center bg-white/5 border border-border ${accent || "text-muted-foreground"}`}>
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </div>
      </div>
      <div className="font-heading text-4xl font-bold tracking-tighter mt-5">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>}
    </div>
  );
}

function Gauge({ label, value, color }) {
  return (
    <div className="card-elevated p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-muted-foreground uppercase tracking-widest">{label}</span>
        <span className="font-mono text-xl" style={{ color }}><CountUp value={value} />%</span>
      </div>
      <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, background: color, boxShadow: `0 0 12px ${color}66` }} />
      </div>
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div className="p-8 font-mono text-xs text-muted-foreground">LOADING TELEMETRY…</div>;

  return (
    <div>
      <PageHeader eyebrow="COMMAND CENTER" title="Overview" subtitle="Real-time protection posture across your regulated fleet" />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Database} label="Workloads" value={<CountUp value={stats.total_workloads} />} sub={`${stats.protected} protected`} accent="text-[hsl(var(--chart-1))]" />
          <Stat icon={HardDrive} label="Restore Points" value={<CountUp value={stats.restore_points} />} sub={`${stats.storage_gb} GB stored`} accent="text-[hsl(var(--chart-2))]" />
          <Stat icon={Radar} label="Active Threats" value={<CountUp value={stats.active_threats} />} sub="requires attention" accent="text-primary" />
          <Stat icon={FileCheck2} label="GxP Records" value={`${stats.gxp_verified}/${stats.gxp_total}`} sub="verified integrity" accent="text-[hsl(var(--chart-2))]" />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Gauge label="Security Posture" value={stats.security_posture} color="#F23F2C" />
          <Gauge label="Compliance Score" value={stats.compliance_score} color="#00FF66" />
        </div>

        <div className="card-elevated p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs text-muted-foreground uppercase tracking-widest flex items-center gap-2"><Activity className="h-4 w-4" strokeWidth={1.5} /> 7-Day Backup & Threat Activity</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F23F2C" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#F23F2C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="day" stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#737373" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #1f1f1f", borderRadius: 4, fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="backups" stroke="#00F0FF" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="threats" stroke="#F23F2C" fill="url(#g2)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-elevated p-6 flex items-center gap-4">
          <div className="h-11 w-11 rounded-sm bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--chart-2))]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm">All critical workloads have a verified restore point within RPO.</p>
            <p className="font-mono text-[11px] text-muted-foreground tracking-widest mt-1">SYSTEM STATUS · OPERATIONAL</p>
          </div>
        </div>
      </div>
    </div>
  );
}
